/**
 * FeedbackButton.js — feedback dialog opened from the Account menu.
 *
 * The dialog has a category dropdown (the four fb.cat.* keys,
 * so the list follows the interface language) and a free-text message field.
 * On submit the message is sent to the backend via `feedbackAPI.submit`.
 *
 * The parent owns visibility; this component handles the feedback form.
 *
 * State:
 * - open:       parent-controlled visibility
 * - category:   string  — selected feedback category
 * - message:    string  — user's free-text message
 * - submitting: boolean — disable UI while the POST is in flight
 * - status:     'ok' | 'err' | null — result of the last submit attempt
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { feedbackAPI } from './api';
import { useI18n } from './i18n';

// ── Inline styles ──────────────────────────────────

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    width: '420px',
    maxWidth: '90vw',
    maxHeight: 'calc(var(--kv-viewport-h) - 24px)',
    overflowY: 'auto',
    background: '#111827',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    padding: '20px',
    color: '#e5e7eb',
    boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    margin: '0 0 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  label: {
    fontSize: '12px',
    color: '#94a3b8',
    marginBottom: '4px',
    display: 'block',
  },
  select: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.2)',
    background: '#0b1220',
    color: '#fff',
    fontSize: '13px',
    marginBottom: '12px',
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.2)',
    background: '#0b1220',
    color: '#fff',
    fontSize: '13px',
    minHeight: '120px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '12px',
  },
  btn: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
  },
  btnClose: {
    background: '#374151',
    color: '#e5e7eb',
  },
  btnSubmit: {
    background: 'linear-gradient(135deg, #38bdf8, #818cf8)',
    color: '#fff',
  },
  btnSubmitDisabled: {
    background: '#374151',
    color: '#64748b',
    cursor: 'not-allowed',
  },
  status: {
    fontSize: '12px',
    marginTop: '8px',
    textAlign: 'center',
  },
  statusOk: { color: '#22c55e' },
  statusErr: { color: '#ef4444' },
};

// Available feedback categories shown in the dropdown
const CATEGORIES = [
  { value: 'suggestion', labelKey: 'fb.cat.suggestion' },
  { value: 'bug', labelKey: 'fb.cat.bug' },
  { value: 'question', labelKey: 'fb.cat.question' },
  { value: 'other', labelKey: 'fb.cat.other' },
];

/**
 * FeedbackButton component.
 *
 * @returns {JSX.Element} The FAB + modal markup.
 */
export default function FeedbackButton({ open = false, onClose = null }) {
  const { t } = useI18n();
  const [category, setCategory] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'ok' | 'err' | null

  /**
   * Submit the feedback message to the backend.
   * On success, shows a confirmation for 1.5 s then closes the modal.
   * @param {Event} e - Form submit event.
   */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim() || submitting) return;

    setSubmitting(true);
    setStatus(null);
    try {
      await feedbackAPI.submit(message.trim(), category);
      setStatus('ok');
      setMessage('');
      setCategory('suggestion');
      setTimeout(() => { onClose?.(); setStatus(null); }, 1500);
    } catch (err) {
      setStatus('err');
    } finally {
      setSubmitting(false);
    }
  }

  /** Close the modal and reset the status indicator. */
  function handleClose() {
    onClose?.();
    setStatus(null);
  }

  return (
    <>
      {/* ── Feedback modal ── */}
      {open && createPortal(
        <div style={styles.backdrop} onClick={handleClose}>
          <div className="kv-feedback-modal" role="dialog" aria-label={t('fb.title')} style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.title}>{t('fb.title')}</div>

            <label style={styles.label}>{t('fb.type')}</label>
            <select
              style={styles.select}
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
              ))}
            </select>

            <label style={styles.label}>{t('fb.message')}</label>
            <textarea
              style={styles.textarea}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={t('fb.placeholder')}
              maxLength={5000}
              autoFocus
            />

            {status === 'ok' && <div style={{ ...styles.status, ...styles.statusOk }}>{t('fb.sent')}</div>}
            {status === 'err' && <div style={{ ...styles.status, ...styles.statusErr }}>{t('fb.failed')}</div>}

            <div style={styles.actions}>
              <button style={{ ...styles.btn, ...styles.btnClose }} onClick={handleClose}>
                {t('fb.close')}
              </button>
              <button
                style={{ ...styles.btn, ...(message.trim() && !submitting ? styles.btnSubmit : styles.btnSubmitDisabled) }}
                onClick={handleSubmit}
                disabled={!message.trim() || submitting}
              >
                {submitting ? t('fb.sending') : t('fb.send')}
              </button>
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
}
